import type { LocalUploadDescriptor, ResumableUploadSession } from './api';

export function validateResumableSession(session: ResumableUploadSession, size: number): void {
  const url = new URL(session.url);
  if (session.transport !== 'resumable' || url.protocol !== 'https:' || url.username || url.password || url.port || url.hash
    || !(url.hostname === 'storage.googleapis.com' || url.hostname.endsWith('.storage.googleapis.com'))
    || session.total_bytes !== size || !Number.isSafeInteger(session.offset) || session.offset < 0 || session.offset > size
    || !Number.isSafeInteger(session.chunk_size) || session.chunk_size <= 0) {
    throw new Error('Storage returned an invalid upload session.');
  }
}

export async function describeUploadFile(file: File): Promise<LocalUploadDescriptor> {
  const sampleSize = 64 * 1024;
  const last = Math.max(0, file.size - sampleSize);
  const samples = new Blob(Array.from(new Set([0, Math.floor(last / 2), last])).map((start) => file.slice(start, start + sampleSize)));
  const digest = await crypto.subtle.digest('SHA-256', await samples.arrayBuffer());
  return { file_name: file.name, content_type: file.type || 'application/octet-stream', size_bytes: file.size,
    last_modified: file.lastModified, sample_sha256: Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('') };
}

export async function transferResumableFile(file: File, session: ResumableUploadSession, signal: AbortSignal,
  onProgress: (bytes: number, confirmed: number) => void = () => {}): Promise<void> {
  validateResumableSession(session, file.size);
  let offset = session.offset;
  onProgress(offset, offset);
  while (offset < file.size) {
    if (signal.aborted) throw new DOMException('Upload paused.', 'AbortError');
    const start = offset;
    const end = Math.min(file.size, start + Math.min(session.chunk_size, 8 * 1024 * 1024));
    const confirmed = await putResumableChunk(session.url, file.slice(start, end), start, file.size, signal,
      (sent) => onProgress(start + sent, start));
    if (confirmed <= start || confirmed > end) throw new Error('Storage did not confirm the uploaded bytes. Resume to try again.');
    offset = confirmed;
    onProgress(offset, offset);
  }
}

export function putResumableChunk(url: string, chunk: Blob, offset: number, total: number, signal: AbortSignal, onProgress: (sent: number) => void): Promise<number> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const cleanup = () => signal.removeEventListener('abort', abort);
    xhr.open('PUT', url); xhr.timeout = 5 * 60 * 1000;
    xhr.setRequestHeader('Content-Range', `bytes ${offset}-${offset + chunk.size - 1}/${total}`);
    xhr.upload.onprogress = (event) => onProgress(Math.min(event.loaded, chunk.size));
    xhr.onload = () => {
      cleanup();
      if (xhr.status === 200 || xhr.status === 201) resolve(total);
      else if (xhr.status === 308) {
        const range = xhr.getResponseHeader('Range');
        const match = range?.match(/^bytes=0-(\d+)$/);
        resolve(match ? Number(match[1]) + 1 : 0);
      } else reject(new Error(`Storage upload failed (${xhr.status}). Resume to continue.`));
    };
    xhr.onerror = xhr.ontimeout = () => { cleanup(); reject(new Error('Upload connection interrupted. Resume to continue.')); };
    xhr.onabort = () => { cleanup(); reject(new DOMException('Upload paused.', 'AbortError')); };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) { cleanup(); reject(new DOMException('Upload paused.', 'AbortError')); return; }
    xhr.send(chunk);
  });
}
